import React from "react";
import { Building2, FileText, Loader2, Stethoscope, User } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { EntityInfoBlock } from "@/components/companies/entity-ui";
import { formatCiroDate, formatCiroMoney } from "@/utils/ciroHelpers";

const DetailRow = ({ label, value, isDark }) => (
  <div className="flex justify-between gap-3 text-xs py-1 border-b border-dashed last:border-0 border-opacity-40">
    <span className={isDark ? "text-slate-400" : "text-gray-500"}>{label}</span>
    <span className={`text-right font-medium ${isDark ? "text-slate-100" : "text-gray-900"}`}>{value || "—"}</span>
  </div>
);

const CiroOperationDetailModal = ({ open, onOpenChange, loading, data, isDark, textClass, labelClass }) => {
  const op = data?.operation;
  const payments = data?.hospital_payments || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-3xl w-[calc(100%-2rem)] max-h-[min(90vh,820px)] !flex !flex-col gap-0 p-0 overflow-hidden sm:rounded-2xl ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white border-gray-200"}`}>
        <DialogHeader className={`px-6 pt-6 pb-4 pr-14 border-b shrink-0 text-left ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <DialogTitle className={`flex items-center gap-2 text-base font-semibold ${textClass}`}>
            <User className="w-4 h-4 text-emerald-500" />
            {op?.patient_name || "İşlem Detayı"}
          </DialogTitle>
          <DialogDescription className={`text-xs mt-1 ${labelClass}`}>
            {formatCiroDate(op?.operation_date)} · {op?.doctor_name || op?.doctor_name_raw || "—"}
            {op?.patient_status ? (
              <Badge className="ml-2 bg-amber-500/20 text-amber-600 border-amber-500/30">{op.patient_status}</Badge>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
          ) : !op ? (
            <p className={`text-sm ${labelClass}`}>Kayıt bulunamadı</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div className={`rounded-lg border p-2 ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-100 bg-gray-50"}`}>
                  <p className={`text-[10px] uppercase ${labelClass}`}>Toplam</p>
                  <p className="text-sm font-bold text-emerald-600">{formatCiroMoney(op.total_payment)}</p>
                </div>
                <div className={`rounded-lg border p-2 ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-100 bg-gray-50"}`}>
                  <p className={`text-[10px] uppercase ${labelClass}`}>Hastane</p>
                  <p className="text-sm font-bold">{formatCiroMoney(op.hospital_payment_total)}</p>
                </div>
                <div className={`rounded-lg border p-2 ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-100 bg-gray-50"}`}>
                  <p className={`text-[10px] uppercase ${labelClass}`}>Klinik</p>
                  <p className="text-sm font-bold">{formatCiroMoney(op.doctor_patient_payments)}</p>
                </div>
                <div className={`rounded-lg border p-2 ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-100 bg-gray-50"}`}>
                  <p className={`text-[10px] uppercase ${labelClass}`}>Agent Fiyat</p>
                  <p className="text-sm font-bold">{op.agent_price_range || "—"}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <EntityInfoBlock title="Ciro Takip Bilgileri" icon={Stethoscope} tone="emerald" isDark={isDark}>
                  <DetailRow label="Tarih" value={formatCiroDate(op.operation_date)} isDark={isDark} />
                  <DetailRow label="Hasta" value={op.patient_name} isDark={isDark} />
                  <DetailRow label="Doktor (Excel)" value={op.doctor_name_raw} isDark={isDark} />
                  <DetailRow label="Doktor (Sistem)" value={op.doctor_name} isDark={isDark} />
                  <DetailRow label="Agent" value={op.agent_name} isDark={isDark} />
                  <DetailRow label="Satışçı" value={op.seller} isDark={isDark} />
                  <DetailRow label="Bölge" value={op.body_region} isDark={isDark} />
                  <DetailRow label="İşlem" value={op.procedure_name} isDark={isDark} />
                  <DetailRow label="Anestezi" value={op.anesthesia_type} isDark={isDark} />
                  <DetailRow label="Ödeme Şekli" value={op.payment_method} isDark={isDark} />
                </EntityInfoBlock>

                <EntityInfoBlock title="Ücret Kırılımı" icon={FileText} tone="sky" isDark={isDark}>
                  <DetailRow label="Muayene" value={formatCiroMoney(op.hospital_exam_fee)} isDark={isDark} />
                  <DetailRow label="Anestezi/Oda" value={formatCiroMoney(op.anesthesia_room_fee)} isDark={isDark} />
                  <DetailRow label="Hastane Toplam" value={formatCiroMoney(op.hospital_payment_total)} isDark={isDark} />
                  <DetailRow label="Doktor/Hasta" value={formatCiroMoney(op.doctor_patient_payments)} isDark={isDark} />
                  <DetailRow label="Genel Toplam" value={formatCiroMoney(op.total_payment)} isDark={isDark} />
                  <DetailRow label="Hastane" value={op.hospital_name} isDark={isDark} />
                </EntityInfoBlock>
              </div>

              {(op.patient_status || op.detail_notes || payments.length > 0) && (
                <EntityInfoBlock title="Ataşehir Hasta ve Ödeme Detayı" icon={Building2} tone="amber" isDark={isDark}>
                  {op.patient_status && (
                    <DetailRow label="Hasta Durumu" value={op.patient_status} isDark={isDark} />
                  )}
                  {op.agent_price_range && (
                    <DetailRow label="Agent Fiyat Aralığı" value={op.agent_price_range} isDark={isDark} />
                  )}
                  {op.detail_notes && (
                    <div className={`mt-2 text-xs rounded-lg p-2 ${isDark ? "bg-slate-800/60 text-slate-200" : "bg-amber-50 text-gray-800"}`}>
                      <p className={`text-[10px] uppercase mb-1 ${labelClass}`}>Not</p>
                      <p className="whitespace-pre-wrap">{op.detail_notes}</p>
                    </div>
                  )}
                  {payments.map((pay) => (
                    <div key={pay.id} className={`mt-3 pt-3 border-t ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                      <DetailRow label="Ödeme Tarihi" value={formatCiroDate(pay.payment_date)} isDark={isDark} />
                      <DetailRow label="Alınan Ücret" value={formatCiroMoney(pay.amount)} isDark={isDark} />
                      <DetailRow label="Agent" value={pay.agent_name} isDark={isDark} />
                      <DetailRow label="Agent Fiyat" value={pay.agent_price_range} isDark={isDark} />
                      <DetailRow label="Satışçı" value={pay.seller} isDark={isDark} />
                      <DetailRow label="Ödeme Şekli" value={pay.payment_method} isDark={isDark} />
                      <DetailRow label="Durum" value={pay.patient_status} isDark={isDark} />
                      {pay.notes && (
                        <p className={`text-xs mt-1 ${isDark ? "text-slate-300" : "text-gray-700"}`}>{pay.notes}</p>
                      )}
                    </div>
                  ))}
                </EntityInfoBlock>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CiroOperationDetailModal;
